import { MAIN_THREAD_ID } from '../../api/types';
import { getGlobal, getActions, setGlobal } from '../../global';
import {
  replaceThreadParam,
  safeReplaceViewportIds,
  updateCurrentMessageList,
} from '../../global/reducers';
import { selectCurrentMessageList, selectListedIds } from '../../global/selectors';
import type { GlobalState } from '../../global/types';
import type { ThreadId } from '../../types';
import { getCurrentTabId } from '../establishMultitabRole';

type RenderStormHarnessSnapshot = {
  version: 1;
  sourceTabId?: number;
  global: GlobalState;
  meta?: {
    chatId?: string;
    threadId?: ThreadId;
    createdAt?: string;
  };
};

type LoadSnapshotResult = {
  localTabId: number;
  sourceTabId: number;
  chatId?: string;
  threadId?: ThreadId;
};

type ReplayScenarioOptions = {
  chatId: string;
  threadId?: ThreadId;
  tabId?: number;
  seedCount?: number;
  chunkSize?: number;
  viewportMode?: 'all-listed' | 'tail-window';
  viewportTailCount?: number;
  replayEveryMs?: number;
  durationMs?: number;
  stepMs?: number;
  warmupMs?: number;
  scrollPattern?: 'triangle';
};

type ReplayScenarioResult = {
  grade: 'green' | 'yellow' | 'red';
  avgMs: number;
  maxMs: number;
  frames: number;
  over33: number;
  over33Pct: number;
  longTaskMax: number;
  longTaskCount: number;
  listed: number;
  viewport: number;
  scrollHeight: number;
  clientHeight: number;
  appliedChunks: number;
};

type ReplayRunState = {
  status: 'idle' | 'running' | 'completed' | 'failed';
  runId?: string;
  options?: ReplayScenarioOptions;
  result?: ReplayScenarioResult;
  error?: string;
  baseGlobal?: GlobalState;
};

const DEFAULT_SEED_COUNT = 20;
const DEFAULT_CHUNK_SIZE = 48;
const DEFAULT_VIEWPORT_TAIL_COUNT = 36;
const DEFAULT_REPLAY_EVERY_MS = 180;
const DEFAULT_DURATION_MS = 7000;
const DEFAULT_STEP_MS = 16;
const DEFAULT_WARMUP_MS = 150;

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function nextFrame() {
  return new Promise<number>((resolve) => {
    requestAnimationFrame(resolve);
  });
}

function resolveSourceTabId(snapshot: RenderStormHarnessSnapshot) {
  if (snapshot.sourceTabId !== undefined) {
    return snapshot.sourceTabId;
  }

  const masterTab = Object.values(snapshot.global.byTabId).find((tab) => tab.isMasterTab);
  if (masterTab) {
    return masterTab.id;
  }

  const firstKey = Object.keys(snapshot.global.byTabId)[0];
  if (firstKey) {
    return Number(firstKey);
  }

  throw new Error('snapshot has no tab state');
}

function resolveLocalTabId(global: GlobalState) {
  const current = getCurrentTabId();
  if (global.byTabId[current]) {
    return current;
  }

  const firstKey = Object.keys(global.byTabId)[0];
  if (firstKey) {
    return Number(firstKey);
  }

  return current;
}

function normalizeSnapshot(input: RenderStormHarnessSnapshot | string) {
  const snapshot = typeof input === 'string'
    ? JSON.parse(input) as RenderStormHarnessSnapshot
    : input;

  if (!snapshot || snapshot.version !== 1 || !snapshot.global) {
    throw new Error('invalid render storm snapshot');
  }

  return snapshot;
}

function summarizeCurrentMessageList(global: GlobalState) {
  const tabId = resolveLocalTabId(global);
  const currentMessageList = selectCurrentMessageList(global, tabId);
  return {
    tabId,
    chatId: currentMessageList?.chatId,
    threadId: currentMessageList?.threadId,
  };
}

function queryActiveScroller() {
  return Array.from(document.querySelectorAll<HTMLElement>('.Transition.MessageList.custom-scroll.with-default-bg'))
    .find((el) => el.clientHeight > 0 && el.scrollHeight > el.clientHeight + 20);
}

async function waitForScroller(timeoutMs = 5000) {
  const start = performance.now();

  while (performance.now() - start < timeoutMs) {
    const scroller = queryActiveScroller();
    if (scroller) {
      return scroller;
    }

    await wait(50);
  }

  return undefined;
}

async function waitForListedLength(
  chatId: string,
  threadId: ThreadId,
  expectedLength: number,
  timeoutMs = 2000,
) {
  const start = performance.now();

  while (performance.now() - start < timeoutMs) {
    const currentLength = selectListedIds(getGlobal(), chatId, threadId)?.length || 0;
    if (currentLength >= expectedLength) {
      return true;
    }

    await wait(50);
  }

  return false;
}

function buildViewportIds(
  listedIds: number[],
  viewportMode: 'all-listed' | 'tail-window',
  viewportTailCount: number,
) {
  if (viewportMode === 'all-listed') {
    return listedIds;
  }

  return listedIds.slice(-viewportTailCount);
}

function applySeedState(
  global: GlobalState,
  chatId: string,
  threadId: ThreadId,
  tabId: number,
  seedIds: number[],
  viewportIds: number[],
) {
  let next = updateCurrentMessageList(global, chatId, threadId, 'thread', true, true, tabId);
  next = replaceThreadParam(next, chatId, threadId, 'listedIds', seedIds);
  next = replaceThreadParam(next, chatId, threadId, 'outlyingLists', undefined);
  next = replaceThreadParam(next, chatId, threadId, 'lastScrollOffset', undefined);
  next = replaceThreadParam(next, chatId, threadId, 'lastViewportIds', viewportIds);
  next = safeReplaceViewportIds(next, chatId, threadId, viewportIds, tabId);
  return next;
}

function classifyRun(avgMs: number, maxMs: number, over33Pct: number) {
  if (avgMs > 30 || maxMs > 500 || over33Pct > 10) {
    return 'red' as const;
  }

  if (avgMs < 20 && maxMs < 100 && over33Pct < 2) {
    return 'green' as const;
  }

  return 'yellow' as const;
}

function attachHarness() {
  const state: ReplayRunState = {
    status: 'idle',
  };

  const api = {
    loadSnapshot(snapshotInput: RenderStormHarnessSnapshot | string): LoadSnapshotResult {
      const snapshot = normalizeSnapshot(snapshotInput);
      const localGlobal = getGlobal();
      const localTabId = resolveLocalTabId(localGlobal);
      const sourceTabId = resolveSourceTabId(snapshot);
      const sourceTabState = snapshot.global.byTabId[sourceTabId];

      if (!sourceTabState) {
        throw new Error(`snapshot missing source tab ${sourceTabId}`);
      }

      const next = deepClone(snapshot.global);
      next.byTabId = {
        [localTabId]: {
          ...sourceTabState,
          id: localTabId,
          isMasterTab: true,
        },
      };

      state.baseGlobal = deepClone(next);
      setGlobal(next);

      const summary = summarizeCurrentMessageList(next);
      return {
        localTabId,
        sourceTabId,
        chatId: summary.chatId,
        threadId: summary.threadId,
      };
    },

    exportSnapshot(meta?: { chatId?: string; threadId?: ThreadId }) {
      const global = getGlobal();
      return {
        version: 1 as const,
        sourceTabId: resolveLocalTabId(global),
        global: deepClone(global),
        meta: {
          chatId: meta?.chatId,
          threadId: meta?.threadId,
          createdAt: new Date().toISOString(),
        },
      };
    },

    async runReplay(options: ReplayScenarioOptions): Promise<ReplayScenarioResult> {
      const {
        chatId,
        threadId = MAIN_THREAD_ID,
        seedCount = DEFAULT_SEED_COUNT,
        chunkSize = DEFAULT_CHUNK_SIZE,
        viewportMode = 'all-listed',
        viewportTailCount = DEFAULT_VIEWPORT_TAIL_COUNT,
        replayEveryMs = DEFAULT_REPLAY_EVERY_MS,
        durationMs = DEFAULT_DURATION_MS,
        stepMs = DEFAULT_STEP_MS,
        warmupMs = DEFAULT_WARMUP_MS,
      } = options;

      if (state.baseGlobal) {
        setGlobal(deepClone(state.baseGlobal));
        await wait(warmupMs);
      }

      const sourceGlobal = state.baseGlobal ?? getGlobal();
      const currentGlobal = getGlobal();
      const tabId = options.tabId ?? summarizeCurrentMessageList(currentGlobal).tabId;

      const fullListedIds = [...(selectListedIds(sourceGlobal, chatId, threadId) || [])];
      if (!fullListedIds.length) {
        throw new Error(`no listed ids available for ${chatId}/${threadId}`);
      }

      await waitForListedLength(chatId, threadId, fullListedIds.length, 1500);

      const seedIds = fullListedIds.slice(-Math.min(seedCount, fullListedIds.length));
      const seedViewportIds = buildViewportIds(seedIds, viewportMode, viewportTailCount);

      let next = applySeedState(getGlobal(), chatId, threadId, tabId, seedIds, seedViewportIds);
      setGlobal(next);
      await nextFrame();
      await wait(warmupMs);

      const scroller = await waitForScroller();
      if (!scroller) {
        throw new Error('no active message list scroller found');
      }

      const frameDeltas: number[] = [];
      const longTasks: Array<{ start: number; dur: number }> = [];
      let observer: PerformanceObserver | undefined;

      if (window.PerformanceObserver
        && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
        observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            longTasks.push({
              start: Math.round(entry.startTime),
              dur: Math.round(entry.duration),
            });
          });
        });
        observer.observe({ entryTypes: ['longtask'] });
      }

      let lastFrame = performance.now();
      let stopFrames = false;
      const onFrame = (now: number) => {
        frameDeltas.push(now - lastFrame);
        lastFrame = now;
        if (!stopFrames) {
          requestAnimationFrame(onFrame);
        }
      };
      requestAnimationFrame(onFrame);

      const totalHiddenCount = Math.max(0, fullListedIds.length - seedIds.length);
      const totalChunks = Math.ceil(totalHiddenCount / Math.max(chunkSize, 1));
      let appliedChunks = 0;
      let lastChunkAt = performance.now();
      const start = performance.now();

      while (performance.now() - start < durationMs) {
        const elapsed = performance.now() - start;
        const progress = elapsed / durationMs;
        const triangle = progress < 0.5 ? progress * 2 : (1 - (progress - 0.5) * 2);
        scroller.scrollTop = Math.round((scroller.scrollHeight - scroller.clientHeight) * triangle);

        if (appliedChunks < totalChunks && performance.now() - lastChunkAt >= replayEveryMs) {
          appliedChunks += 1;
          lastChunkAt = performance.now();
        }

        const visibleCount = Math.min(fullListedIds.length, seedIds.length + appliedChunks * chunkSize);
        const listedIds = fullListedIds.slice(-visibleCount);
        const viewportIds = buildViewportIds(listedIds, viewportMode, viewportTailCount);
        const currentListedCount = selectListedIds(getGlobal(), chatId, threadId)?.length || 0;
        if (currentListedCount !== listedIds.length) {
          next = applySeedState(getGlobal(), chatId, threadId, tabId, listedIds, viewportIds);
          setGlobal(next);
        }

        await wait(stepMs);
      }

      stopFrames = true;
      await wait(100);
      observer?.disconnect();

      const measuredFrames = frameDeltas.slice(5);
      const avgMs = measuredFrames.reduce((sum, value) => sum + value, 0) / Math.max(measuredFrames.length, 1);
      const maxMs = Math.max(...measuredFrames, 0);
      const over33 = measuredFrames.filter((value) => value > 33).length;
      const over33Pct = (over33 / Math.max(measuredFrames.length, 1)) * 100;
      const longTaskMax = Math.max(0, ...longTasks.map((entry) => entry.dur));

      return {
        grade: classifyRun(avgMs, maxMs, over33Pct),
        avgMs: Math.round(avgMs * 10) / 10,
        maxMs: Math.round(maxMs * 10) / 10,
        frames: measuredFrames.length,
        over33,
        over33Pct: Math.round(over33Pct * 10) / 10,
        longTaskMax,
        longTaskCount: longTasks.length,
        listed: selectListedIds(getGlobal(), chatId, threadId)?.length || 0,
        viewport: buildViewportIds(
          selectListedIds(getGlobal(), chatId, threadId) || [],
          viewportMode,
          viewportTailCount,
        ).length,
        scrollHeight: scroller.scrollHeight,
        clientHeight: scroller.clientHeight,
        appliedChunks,
      };
    },

    startReplay(options: ReplayScenarioOptions) {
      const runId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      state.status = 'running';
      state.runId = runId;
      state.options = deepClone(options);
      state.result = undefined;
      state.error = undefined;

      void api.runReplay(options)
        .then((result) => {
          state.status = 'completed';
          state.result = result;
        })
        .catch((error: unknown) => {
          state.status = 'failed';
          state.error = error instanceof Error ? error.message : String(error);
        });

      return {
        runId,
        status: state.status,
      };
    },

    getStatus() {
      return deepClone({
        status: state.status,
        runId: state.runId,
        options: state.options,
        result: state.result,
        error: state.error,
      });
    },
  };

  (window as any).__renderStormHarness = api;
}

attachHarness();
